import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import { environment } from '@environment';
import { AuthService } from '@app/services/auth.service';
import { MobileDeviceService } from '@app/services/mobile-device.service';

import { userAddressesApi, userApi } from '@app/modules/core/api/user.api';
import { abandonedReservationApi } from '@abandoned-reservation/api/abandoned-reservation.api';
import { bookingApi } from '@booking/booking.api';
import { busResultsApi } from '@buses/bus-results/bus-results.api';
import { widgetsApi } from '@shared/widgets/widgets.api';
import { userHistoryApi } from '@user-history/user-history.api';
import { billingApi } from '@reservation/billing/billing.api';
import { slackPushApi } from '@notifications/slack/api/slack-push.api';
import { assistanceApi } from '@shared/travel-assistance/assistance.api';
import { seatMapApi } from '@seat-map/seat-map.api';
import { paxApi } from '@reservation/pax/pax.api';
import { recentApi } from '@recent-searches/recent.api';
import { flightResultsApi } from '@flight-results/flight-results.api';
import { homeItemDetailApi } from '@home-item-detail/home-item-detail.api';
import { searchBarApi } from '@search-bar/search-bar.api';
import { homeApi } from '@home/home.api';

const URLS_REQUERING_AUTH = [
  abandonedReservationApi.retarget.get_retarget,
  abandonedReservationApi.cancel,
  bookingApi.air_validation,
  bookingApi.air_book,
  bookingApi.package_validate,
  bookingApi.package_book,
  bookingApi.activities_book,
  bookingApi.activities_validate,
  bookingApi.auto_validation,
  bookingApi.auto_book,
  bookingApi.hotel_validation,
  bookingApi.hotel_validation_byhours,
  bookingApi.hotel_book,
  bookingApi.exchange_rate,
  bookingApi.rappi_payment,
  bookingApi.terms_and_conditions,
  bookingApi.rappi_intermediary_payment,
  userApi.data.url,
  userApi.info.url,
  userApi.get_benefits.url,
  userAddressesApi.data.url,
  userAddressesApi.cities.url,
  userAddressesApi.active.url,
  userAddressesApi.create.url,
  widgetsApi.alerts.get_user_alerts,
  widgetsApi.alerts.get_alert,
  widgetsApi.alerts.create_alert,
  widgetsApi.alerts.delete_alert,
  widgetsApi.alerts.searches.create_alert_search,
  userHistoryApi.userhistory,
  userHistoryApi.userDetailBooking,
  userHistoryApi.history_detail.last,
  userHistoryApi.air_policies,
  userHistoryApi.cancellation,
  userHistoryApi.resend_voucher,
  userHistoryApi.history_detail.get_detail_by_validate,
  userHistoryApi.history_detail.get_detail_by_id,
  userHistoryApi.history_detail.validate_payment_itinerary,
  userHistoryApi.rappi_payment,
  userHistoryApi.air_exchange_book,
  billingApi.get_billing,
  billingApi.default_billing,
  billingApi.list_by_user,
  billingApi.create_billing,
  billingApi.update_billing,
  billingApi.delete_billing,
  billingApi.travel_order_billing.create_order_billing,
  slackPushApi.slack_push_notification,
  assistanceApi.assistance_search,
  assistanceApi.assistance_book,
  assistanceApi.assistance_cancellation,
  seatMapApi.itineraryDetail,
  paxApi.pax_traveler_list,
  paxApi.pax_create,
  paxApi.pax_update,
  paxApi.pax_delete,
  paxApi.loyalty.pax_loyalty_create,
  paxApi.loyalty.pax_loyalty_update,
  paxApi.loyalty.pax_loyalty_delete,
  paxApi.document.pax_document_create,
  paxApi.document.pax_document_update,
  paxApi.document.pax_document_delete,
  recentApi.home_searches_recent,
  recentApi.home_searches_recent_delete,
  recentApi.air_save_searches,
  recentApi.bus_save_searches,
  recentApi.hotel_save_searches,
  recentApi.auto_save_searches,
  recentApi.air_packages_save_searches,
  flightResultsApi.rappi_intermediary_payment,
  homeItemDetailApi.home_flight_offers,
  busResultsApi.bus_filter,
  homeApi.home_data
];

const URLS_WITH_ENCODED_CONTENT_TYPE = [
  widgetsApi.alerts.create_alert,
  widgetsApi.alerts.delete_alert,
]

const URLS_EXCLUDING_AUTH = [
  searchBarApi.ip_public.url
];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(
    private authService: AuthService,
    private mobileDeviceService: MobileDeviceService
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const useCookiesAuth = environment.use_cookies_auth;
    const authToken = this.authService.authToken();
    return this.mobileDeviceService.isMobileDevice$.pipe(
      first(),
      map(isMobile => {
        let modifiedReq = req;
        let normalizedUrl = req.url.split('?')[0];

        const isExcluded = URLS_EXCLUDING_AUTH.some(apiUrl => 
          apiUrl?.includes(normalizedUrl) || this.matchUrlPattern(apiUrl, normalizedUrl));

        if (isExcluded) {
          return modifiedReq;
        }

        const shouldAuthenticate = URLS_REQUERING_AUTH.some(apiUrl => 
          apiUrl?.includes(normalizedUrl) || this.matchUrlPattern(apiUrl, normalizedUrl));

        const hasEncondedContentType = URLS_WITH_ENCODED_CONTENT_TYPE.some(apiUrl => 
          apiUrl?.includes(normalizedUrl)
        );
        
        if (shouldAuthenticate) {
          modifiedReq = this.handleAuthentication(modifiedReq, authToken, isMobile, useCookiesAuth);
        }

        modifiedReq = this.applyCommonHeaders(modifiedReq, hasEncondedContentType);
        return modifiedReq;
      }),
      switchMap(modifiedReq => next.handle(modifiedReq))
    );
  }

  private handleAuthentication(
    req: HttpRequest<unknown>, 
    authToken: string | null, 
    isMobile: boolean, 
    useCookiesAuth: boolean
  ): HttpRequest<unknown> {
    if (!isMobile && useCookiesAuth) {
      return req.clone({ withCredentials: true });
    } else if(isMobile || (authToken && !req.headers.has('Authorization'))) {
      return this.addAuthorizationHeader(req, authToken);
    } else {
      return req;
    }
  }

  private addAuthorizationHeader(req: HttpRequest<unknown>, authToken: string | null): HttpRequest<unknown> {
    if (authToken) {
      return req.clone({
        setHeaders: {
          Authorization: `Bearer ${authToken}`
        }
      });
    }
    return req;
  }

  private applyCommonHeaders(req: HttpRequest<unknown>, encodedContentType: boolean): HttpRequest<unknown> {
    return req.clone({
      setHeaders: {
        'Content-Type': encodedContentType ? 'application/x-www-form-urlencoded; charset=UTF-8' : 'application/json',
        'accept-language': environment.language
      }
    });
  }

  private matchUrlPattern(urlPattern: string, requestUrl: string): boolean {
    const regexPattern = urlPattern
      .replace(/\{[^}]+\}/g, '[^/]+')
      .replace(/\//g, '\\/');
    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(requestUrl);
  }
}
